import {
  Box,
  Flex,
  Skeleton,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import GCPImage from "components/GCPImage";
import {
  addDays,
  addHours,
  addMinutes,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  parseISO,
} from "date-fns";
import useDeepEffect from "hooks/useDeepEffect";
import { useSession } from "hooks/useSession";
import {
  COLORS_URL,
  carVersionI18n,
  reduceCarsToOneLevelAddVersion,
} from "lib/cars";
import { getCookie } from "lib/common";
import fsEvent from "lib/firestoreEventTracking";
import {
  addBufferHoursET,
  addBufferHoursST,
  latestOrderTimeWithBusinessTime,
} from "lib/order";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { DateObject } from "react-multi-date-picker";
import appStore from "store";
import api from "store/api";
import * as ga from "../../lib/analytics";
import MainButton from "../MainButton";

const CarListLoading = () => {
  return (
    <VStack align="center">
      <Wrap spacing="16px" justify="center">
        <WrapItem>
          <Skeleton w="358px" h="20px" />
        </WrapItem>
        <WrapItem>
          <Box w="358px" />
        </WrapItem>
        <WrapItem>
          <Box w="358px" />
        </WrapItem>
        <WrapItem>
          <Box w="358px" />
        </WrapItem>
      </Wrap>

      <Wrap spacing="16px" justify="center">
        {Array.from({ length: 12 }).map((_, index) => (
          <WrapItem key={index}>
            <Skeleton borderRadius="16px" w="358px" h="284px" />
          </WrapItem>
        ))}
      </Wrap>
    </VStack>
  );
};

// 符合最快下訂時間(考量營業時間)
const filterNonMinDate = (avaiableCars, { dateTimeObj }) => {
  let result = avaiableCars;
  const { startDate, startTime } = dateTimeObj;
  const now = new Date();
  const orderStart = new Date(`${startDate.format("YYYY/MM/DD")} ${startTime}`);
  const startToNowDiffMins = differenceInMinutes(orderStart, now);

  result = avaiableCars.filter((car) => {
    return (
      startToNowDiffMins >
      latestOrderTimeWithBusinessTime(
        car?.owner_settings?.latest_order_time / 60,
        Number(car?.owner_settings?.business_start_hour.split(":")[0]),
        Number(car?.owner_settings?.business_end_hour.split(":")[0])
      )
    );
  });
  return result;
};

const filterNonBusinessTime = (avaiableCars, { dateTimeObj }) => {
  const checkBetween = (m, rangeStart, rangeEnd) => {
    return m >= rangeStart && m <= rangeEnd;
  };
  const { startTime, endTime } = dateTimeObj;
  const start = Number(startTime.split(":")[0]);
  const end = Number(endTime.split(":")[0]);
  return avaiableCars.filter((car) => {
    const bStart = Number(
      car?.owner_settings?.business_start_hour.split(":")[0]
    );
    const bEnd = Number(car?.owner_settings?.business_end_hour.split(":")[0]);
    if (bStart > bEnd) {
      return (
        (checkBetween(start, bStart, 23) || checkBetween(start, 0, bEnd)) &&
        (checkBetween(end, bStart, 23) || checkBetween(end, 0, bEnd))
      );
    } else {
      return (
        checkBetween(start, bStart, bEnd) && checkBetween(end, bStart, bEnd)
      );
    }
  });
};

const filterNonOrderAndStaffOk = (
  avaiableCars,
  { configs, dateTimeObj, orders }
) => {
  let result = avaiableCars;
  const { startDate, startTime, endDate, endTime } = dateTimeObj;
  const userStart = new Date(startDate.format("YYYY/MM/DD") + " " + startTime);
  const userEnd = new Date(endDate.format("YYYY/MM/DD") + " " + endTime);
  let staffByOwners = {};

  orders.forEach((o) => {
    const theOwnerSetting = configs.find((c) => c.car_ids.includes(o.car_id));
    if (theOwnerSetting) {
      const {
        business_start_hour,
        business_end_hour,
        order_interval,
        owner_id,
      } = theOwnerSetting;
      // console.log("===== 分隔線 =====");
      // console.log("B S~E", business_start_hour, business_end_hour);
      // console.log("Interval: ", order_interval / 60);
      // console.log("Original Start: ", addHours(parseISO(o.start_time), 8));
      // console.log("Original End: ", addHours(parseISO(o.end_time), 8));
      // TODO: 目前沒有考慮到極端 BStart > BEnd (11:00 ~ 04:00) 的例子...

      // 預設9:00上班17:00下班
      const orderBuffStart = addBufferHoursST(
        addHours(parseISO(o.start_time), 8),
        order_interval / 60,
        Number(business_start_hour.split(":")[0]),
        Number(business_end_hour.split(":")[0])
      );
      const orderBuffEnd = addBufferHoursET(
        addHours(parseISO(o.end_time), 8),
        order_interval / 60,
        Number(business_start_hour.split(":")[0]),
        Number(business_end_hour.split(":")[0])
      );
      // console.log("Buffed Start: ", orderBuffStart);
      // console.log("Buffed End: ", orderBuffEnd);

      if (
        !(
          isAfter(userStart, addMinutes(orderBuffEnd, -1)) ||
          isBefore(userEnd, addMinutes(orderBuffStart, 1))
        )
      ) {
        result = result.filter((car) => car.id !== o.car_id);
      }

      // 計算人力
      const startBufferBefore = addHours(parseISO(o.start_time), 7);
      const startHour = addHours(parseISO(o.start_time), 8);
      const startBufferAfter = addHours(parseISO(o.start_time), 9);
      const endBufferBefore = addHours(parseISO(o.end_time), 7);
      const endHour = addHours(parseISO(o.end_time), 8);
      const endBufferAfter = addHours(parseISO(o.end_time), 9);

      if (staffByOwners[owner_id]) {
        staffByOwners[owner_id].push(startBufferBefore);
        staffByOwners[owner_id].push(startHour);
        staffByOwners[owner_id].push(startBufferAfter);
        staffByOwners[owner_id].push(endBufferBefore);
        staffByOwners[owner_id].push(endHour);
        staffByOwners[owner_id].push(endBufferAfter);
      } else {
        staffByOwners[owner_id] = [
          startHour,
          endHour,
          startBufferBefore,
          startBufferAfter,
          endBufferBefore,
          endBufferAfter,
        ];
      }
    }
  });

  // 去除不符合人力
  result = result.filter((car) => {
    const theOwnerSetting = car.owner_settings;
    if (theOwnerSetting) {
      const { staff_count, owner_id } = theOwnerSetting;
      const timeWithStaff =
        staffByOwners[owner_id]?.map((datetime) =>
          format(datetime, "yyyy/MM/dd HH:mm")
        ) || [];

      const startMatches = timeWithStaff.filter(
        (time) => time === format(userStart, "yyyy/MM/dd HH:mm")
      );
      const endMatches = timeWithStaff.filter(
        (time) => time === format(userEnd, "yyyy/MM/dd HH:mm")
      );
      if (startMatches.length >= staff_count) return false;
      if (endMatches.length >= staff_count) return false;
    }
    return true;
  });
  return result;
};

const filterCountry = (avaiableCars, { zoneId, returnZoneId }) => {
  let result = [];
  avaiableCars.forEach((car) => {
    if (car.owner_settings) {
      const supportZones = car.owner_settings.support_zones?.map((zone) =>
        zone.id.toString()
      );

      if (
        supportZones.includes(zoneId.toString()) &&
        supportZones.includes(returnZoneId.toString())
      ) {
        result.push(car);
      }
    }
  });
  return result;
};

const filterModel = (avaiableCars, { modelsFilter }) => {
  let result = avaiableCars;
  Object.keys(modelsFilter).map((key) => {
    let model_name = key
      .replace("Model", "Model ")
      .replace("(", " (")
      .replace("IONIQ6", "IONIQ 6");
    if (modelsFilter[key] === false) {
      result = result.filter(
        (item) => item.model_name.indexOf(model_name) === -1
      );
    }
  });
  return result;
};

const filterColor = (avaiableCars, { colorSelected }) => {
  let result = avaiableCars;
  // 如果顏色中有 true，則過濾掉 false 的顏色
  if (Object.values(colorSelected).includes(true)) {
    Object.keys(colorSelected).map((key) => {
      if (colorSelected[key] === false) {
        result = result.filter((item) => item.color !== key);
      }
    });
  }

  return result;
};

const sortCars = (avaiableCars, sortBy, top_models, groupPrice) => {
  let result = avaiableCars
    .filter((car) => {
      return !top_models?.includes(car.model_id);
    })
    .map((car) => {
      const model_name = `${car.model_name}${car.version ? ` ${car.version}` : ""}`;
      const groupPriceItem = groupPrice.find(
        (item) => item.model_name === model_name
      );
      if (groupPriceItem) {
        car.group_price = groupPriceItem.tax_amount;
      }
      return car;
    });

  if (sortBy === "low") {
    result.sort((a, b) => {
      return parseFloat(a.group_price) - parseFloat(b.group_price);
    });
  } else {
    result.sort((a, b) => {
      return parseFloat(b.group_price) - parseFloat(a.group_price);
    });
  }
  let topModelReverse = top_models?.reverse();

  topModelReverse.forEach((id) => {
    const car = avaiableCars.filter((car) => car.model_id === id);
    if (car) {
      result.unshift(...car);
    }
  });

  return result;
};

const NoCarGroup = (props) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;

  const {
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    availableCarIds,
    isShowColorChoose,
  } = appStore;

  const {
    carGroup: g,
    note,
    iconSrc,
    title,
    subTitle,
    description,
    displayPrice,
    groupPrice,
    cars,
    isHot,
  } = props;

  const [avgPrice, setAvgPrice] = useState("");
  const carIds = g.map((car) => car.id);

  const colorList = useMemo(() => {
    let list = [];
    g.forEach((car) => {
      if (!list.includes(car.color)) {
        list.push(car.color);
      }
    });
    return list;
  }, [g]);

  useEffect(() => {
    if (Object.keys(groupPrice).length === 0) return;
    const floatData = groupPrice.find((item) => {
      return (
        item.model_name ===
        `${g[0].model_name}${g[0].version ? ` ${g[0].version}` : ""}`
      );
    });

    const floatDiscountAmount = floatData?.float_discount_amount;

    const st = new DateObject(
      selectedDays[0].format("YYYY/MM/DD") + " " + selectedStartClock
    );
    const et = new DateObject(
      selectedDays[1].format("YYYY/MM/DD") + " " + selectedEndClock
    );

    const noTaxTotal = Math.ceil(floatDiscountAmount / 1.05);
    const diffTime = et.valueOf() / 1000 - st.valueOf() / 1000;
    const avgAmount = Math.ceil((noTaxTotal / (diffTime / 60 / 60)) * 24);
    setAvgPrice(avgAmount);
  }, [g, groupPrice, selectedDays, selectedEndClock, selectedStartClock]);

  const modelName = g[0]?.model_name
    ?.replace("Model", "Model ")
    ?.replace("(", " (");
  const carTypeKey = modelName?.replace(/\s/g, "")?.toLowerCase() + "_type";

  return (
    <Box
      display="flex"
      flexDirection="column"
      bg="white"
      p="16px"
      w="358px"
      minH="284px"
      border="1px solid rgba(0, 0, 0, .1)"
      borderRadius="16px"
      pos="relative"
      cursor="pointer"
      align="start"
      onClick={() => {
        appStore.tempSelectedZoneId = null;
        appStore.selectedCarIds = carIds;
        router.push({
          pathname: "/rent/nocar",
          query: {
            ...router.query,
            group: g[0].group_lv1_id,
          },
        });
        fbq("track", "ViewContent");
        ga.event({
          action: "view_item",
        });
      }}
    >
      {/* tag */}
      <Flex mb="16px">
        {isHot && (
          <Flex pos="relative">
            <Flex
              px="8px"
              py="4px"
              borderRadius="13px"
              bg="#BDC3D0"
              mr="8px"
              align="center"
            >
              <Text
                fontSize="12px"
                fontWeight="700"
                lineHeight="16px"
                color="white"
              >
                {t("Car_card_hot")}
              </Text>
            </Flex>
          </Flex>
        )}
        <Flex
          px="8px"
          py="4px"
          h="26px"
          borderRadius="13px"
          bg="#BDC3D0"
          mr="8px"
          pos="relative"
          align="center"
        >
          <Text
            fontSize="12px"
            fontWeight="700"
            lineHeight="16px"
            color="white"
          >
            {t("Rent_card_statetext_noCar")}
          </Text>
        </Flex>
      </Flex>

      <Flex align="center" justify="center" h="145px" pos="relative">
        <Flex
          w="100%"
          h="100%"
          bg="#FFFFFF"
          opacity="0.5"
          pos="absolute"
          top="0"
          left="0"
        ></Flex>
        <GCPImage src={iconSrc} alt="car" w="313px" />
      </Flex>

      <Flex
        flexDir="column"
        minH={isShowColorChoose ? "124px" : "108px"}
        w="auto"
      >
        <Flex flex="1"></Flex>

        {/* colors */}
        <Flex
          w={isShowColorChoose ? colorList.length * 16 + "px" : "0px"}
          mt="8px"
          h={isShowColorChoose ? "16px" : "0px"}
          bg="rgba(0, 0, 0, 0.2)"
          borderRadius="8px"
          align="center"
        >
          {isShowColorChoose &&
            colorList.map((color) => {
              return (
                <Box
                  key={color}
                  w="12px"
                  h="12px"
                  border="1px solid white"
                  borderRadius="6px"
                  mx="2px"
                >
                  <GCPImage src={COLORS_URL[color]} alt={color} />
                </Box>
              );
            })}
        </Flex>

        <Flex justify="space-between" mt="8px">
          <Flex flexDir="column" justify="end">
            <Text
              textAlign="start"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
              color="#333333"
              mb="2px"
            >
              {title}
            </Text>
            <Text
              textAlign="start"
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#748598"
            >
              {subTitle && subTitle.toString().includes("座)")
                ? subTitle.split("座) ")[1] || ""
                : subTitle.toString().includes("Seats)")
                  ? subTitle.split("Seats) ")[1] || ""
                  : subTitle}
            </Text>
            <Box mt="8px">
              <Flex align="center">
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_seats.png" />
                <Text
                  mr="16px"
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {g[0].note.seat}
                  {t("peopleSeat")}
                </Text>
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_car.png" />
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {t(carTypeKey)}
                </Text>
              </Flex>
            </Box>
          </Flex>
          <Flex justify="end" flexDir={{ base: "column", md: "column" }}>
            {/* <Text
              textAlign="right"
              textDecoration="line-through"
              color="#748598"
              fontWeight="400"
              fontSize="14px"
              lineHeight="20px"
            >
              NT$ {displayPrice.origin_price?.toLocaleString()}
            </Text> */}
            <Text
              whiteSpace="nowrap"
              as="span"
              color="#BDC3D0"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
            >
              NT$ {avgPrice?.toLocaleString()} &nbsp;/{t("days")}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* <Flex w="100%">
        <Flex
          zIndex="98"
          w="100%"
          h="40px"
          align="center"
          justify="center"
          border="1px solid #333333"
          borderRadius="8px"
          pos="relative"
        >
          <Text
            fontSize="16px"
            lineHeight="24px"
            fontWeight="700"
            color="#333333"
          >
            {t("Rent_card_button_noCar")}
          </Text>
        </Flex>
      </Flex> */}
    </Box>
  );
};

const CarGroup = (props) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;
  const {
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    availableCarIds,
    isShowColorChoose,
  } = appStore;

  const {
    carGroup: g,
    note,
    iconSrc,
    title,
    subTitle,
    description,
    displayPrice,
    cars,
    groupPrice,
    isHot,
  } = props;

  const [avgPrice, setAvgPrice] = useState("");
  const [adminData, setAdminData] = useState({});

  const colorList = useMemo(() => {
    let list = [];
    g.forEach((car) => {
      if (!list.includes(car.color)) {
        list.push(car.color);
      }
    });
    return list;
  }, [g]);

  const carNum = useMemo(() => {
    // let num = 0;
    // Object.keys(cars).map((color) => {
    //   num = num + cars[color].length;
    // });

    // return num;
    return g.length;
  }, [g]);

  const carIds = useMemo(() => {
    // let list = [];
    const list = g.map((car) => car.id);
    // Object.keys(cars).map((color) => {
    //   cars[color].map((car) => {
    //     list.push(car.id);
    //   });
    // });

    return list;
  }, [g]);

  useEffect(() => {
    if (Object.keys(groupPrice).length === 0) return;
    const floatData = groupPrice.find((item) => {
      return (
        item.model_name ===
        `${g[0].model_name}${g[0].version ? ` ${g[0].version}` : ""}`
      );
    });

    setAdminData(floatData);
    const floatDiscountAmount = floatData?.float_discount_amount;

    const st = new DateObject(
      selectedDays[0].format("YYYY/MM/DD") + " " + selectedStartClock
    );
    const et = new DateObject(
      selectedDays[1].format("YYYY/MM/DD") + " " + selectedEndClock
    );

    const noTaxTotal = Math.ceil(floatDiscountAmount / 1.05);

    const diffTime = et.valueOf() / 1000 - st.valueOf() / 1000;
    const avgAmount = Math.ceil((noTaxTotal / (diffTime / 60 / 60)) * 24);
    setAvgPrice(avgAmount);
  }, [g, groupPrice, selectedDays, selectedEndClock, selectedStartClock]);
  const modelName = g[0]?.model_name
    ?.replace("Model", "Model ")
    ?.replace("(", " (");
  const carTypeKey = modelName?.replace(/\s/g, "")?.toLowerCase() + "_type";

  return (
    <Box
      display="flex"
      flexDirection="column"
      bg="white"
      p="16px"
      w="358px"
      minH="284px"
      border="1px solid rgba(0, 0, 0, .1)"
      borderRadius="16px"
      pos="relative"
      cursor="pointer"
      align="start"
      onClick={() => {
        appStore.selectedCarIds = carIds;
        router.push({
          pathname: "/rent/carInfo",
          query: {
            ...router.query,
            group: g[0].group_lv1_id,
          },
        });
        fbq("track", "ViewContent");
        ga.event({
          action: "view_item",
        });
      }}
    >
      {/* 熱門、免費取消 tag */}
      <Flex mb="16px" h="26px">
        {isHot && (
          <Flex
            px="8px"
            py="4px"
            borderRadius="13px"
            bg="#333333"
            mr="8px"
            align="center"
          >
            <Text
              fontSize="12px"
              fontWeight="700"
              lineHeight="16px"
              color="white"
            >
              {t("Car_card_hot")}
            </Text>
          </Flex>
        )}
        {/* <Flex
          px="8px"
          py="4px"
          borderRadius="13px"
          bg="white"
          border="1px solid #333333"
        >
          <Text
            fontSize="12px"
            fontWeight="700"
            lineHeight="16px"
            color="#333333"
          >
            {t("Car_card_free_cancel")}
          </Text>
        </Flex> */}
      </Flex>

      <Flex align="center" justify="center" h="145px">
        <GCPImage src={iconSrc} alt="car" w="313px" />
      </Flex>

      <Flex
        flexDir="column"
        minH={isShowColorChoose ? "124px" : "108px"}
        w="auto"
      >
        <Flex flex="1"></Flex>

        {/* colors */}
        <Flex
          w={isShowColorChoose ? colorList.length * 16 + "px" : "0px"}
          mt="8px"
          h={isShowColorChoose ? "16px" : "0px"}
          bg="rgba(0, 0, 0, 0.2)"
          borderRadius="8px"
          align="center"
        >
          {isShowColorChoose &&
            colorList.map((color) => {
              return (
                <Box
                  key={color}
                  w="12px"
                  h="12px"
                  border="1px solid white"
                  borderRadius="6px"
                  mx="2px"
                >
                  <GCPImage src={COLORS_URL[color]} alt={color} />
                </Box>
              );
            })}
        </Flex>

        <Flex justify="space-between" mt="8px">
          <Flex flexDir="column" justify="end">
            <Text
              textAlign="start"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
              color="#333333"
              mb="2px"
            >
              {title}
            </Text>
            <Text
              textAlign="start"
              fontSize="14px"
              fontWeight={
                g[0].model_id == 31 || g[0].model_id == 32 ? "700" : "400"
              }
              lineHeight="20px"
              color={
                g[0].model_id == 31 || g[0].model_id == 32
                  ? "#D12E25"
                  : "#333333"
              }
            >
              {subTitle && subTitle.toString().includes("座)")
                ? subTitle.split("座) ")[1] || ""
                : subTitle.toString().includes("Seats)")
                  ? subTitle.split("Seats) ")[1] || ""
                  : subTitle}
            </Text>
            <Box mt="8px">
              <Flex align="center">
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_seats.png" />
                <Text
                  mr="16px"
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {g[0].note.seat}
                  {t("peopleSeat")}
                </Text>

                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_car.png" />
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {t(carTypeKey)}
                </Text>
              </Flex>
            </Box>
          </Flex>
          <Flex justify="end" flexDir={{ base: "column", md: "column" }}>
            {carNum <= 5 && (
              <Flex
                ml="auto"
                w="auto"
                zIndex="99"
                py="2px"
                px="4px"
                bg="#D12E25"
                borderRadius="4px"
                mb="4px"
              >
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="700"
                  color="white"
                >
                  {t("Rent_card_buttonchip_only1")} {carNum}{" "}
                  {t("Rent_card_buttonchip_only2")}
                </Text>
              </Flex>
            )}
            <Text
              mb="2px"
              textAlign="right"
              textDecoration="line-through"
              color="#748598"
              fontWeight="400"
              fontSize="14px"
              lineHeight="20px"
            >
              NT$ {displayPrice.origin_price?.toLocaleString()}
            </Text>
            <Text
              whiteSpace="nowrap"
              as="span"
              color="#333333"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
            >
              NT$ {avgPrice?.toLocaleString()} &nbsp;/{t("days")}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* <Flex w="100%">
        <Flex
          zIndex="98"
          w="100%"
          h="40px"
          align="center"
          justify="center"
          bg="#333333"
          borderRadius="8px"
          pos="relative"
        >
          {carNum <= 5 && (
            <Box
              pos="absolute"
              zIndex="99"
              py="4px"
              px="8px"
              bg="#FF9500"
              right="8px"
              top="8px"
              borderRadius="4px"
            >
              <Text
                fontSize="12px"
                lineHeight="16px"
                fontWeight="700"
                color="white"
              >
                {t("Rent_card_buttonchip_only1")} {carNum}
                {t("Rent_card_buttonchip_only2")}
              </Text>
            </Box>
          )}
          <Text
            fontSize="16px"
            lineHeight="24px"
            fontWeight="700"
            color="white"
          >
            {t("Rent_card_button_bookNow")}
          </Text>
        </Flex>
      </Flex> */}
    </Box>
  );
};

function CarList() {
  const {
    cars,
    orders,
    sortBy,
    // colorSelected,
    modelsFilter,
    configs,
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    selectedZoneId,
    avaiableCarGroups,
    isCarListFetching,
    selectedReturnZoneId,
    carsToTwoLevel2,
    deliveryZones,
    loading,
    top_models,
  } = appStore;
  const session = useSession();
  const router = useRouter();
  const { t, i18n } = useTranslation("common");
  const [groupPrice, setGroupPrice] = useState({});

  const dates = selectedDays.map((date) => date.format("YYYY-MM-DD")).join(",");

  useDeepEffect(() => {
    if (session.isLoading) return;
    // KEEP: 這裡的 console 為 debug 用
    if (
      selectedDays.length === 2 &&
      selectedStartClock &&
      selectedEndClock &&
      orders.length &&
      cars.length &&
      configs.length &&
      selectedZoneId &&
      selectedReturnZoneId &&
      top_models &&
      groupPrice &&
      Object.keys(groupPrice).length > 0
    ) {
      // console.log("orders", orders);
      // console.log("cars", cars);
      // console.log("configs", configs);
      let avaiableCars = cars;
      // console.log("全部車輛", avaiableCars);
      const dateTimeObj = {
        startDate: selectedDays[0],
        startTime: selectedStartClock,
        endDate: selectedDays[1],
        endTime: selectedEndClock,
      };

      const params = {
        cars,
        orders,
        configs,
        dateTimeObj,
        zoneId: selectedZoneId,
        returnZoneId: selectedReturnZoneId,
        modelsFilter,
        // colorSelected,
      };

      let filterResult = {};

      // console.log("zoneId", { selectedZoneId, selectedReturnZoneId });
      // console.log("params", params);
      // Step I: 篩選符合車款
      avaiableCars = filterModel(avaiableCars, params);
      // console.log("1. 篩選符合車款", avaiableCars);
      // 將篩選結果取得 id 存入 filterResult
      filterResult = {
        "1_filterModel": avaiableCars.map((car) => car.id),
      };

      // Step II: 篩選符合車色
      // avaiableCars = filterColor(avaiableCars, params);
      // console.log("2. 篩選符合車色", avaiableCars);
      // filterResult = {
      //   ...filterResult,
      //   "2_filterColor": avaiableCars.map((car) => car.id),
      // };

      // nocar只需要篩選I、II

      let _nocarAvaiableCars = avaiableCars;

      _nocarAvaiableCars = sortCars(
        _nocarAvaiableCars,
        sortBy,
        top_models,
        groupPrice
      );

      appStore.carsToTwoLevel2 =
        reduceCarsToOneLevelAddVersion(_nocarAvaiableCars);

      // Step III: 篩選符合營業地區
      avaiableCars = filterCountry(avaiableCars, params);
      // console.log("3. 篩選符合營業地區", avaiableCars);
      filterResult = {
        ...filterResult,
        "3_filterCountry": avaiableCars.map((car) => car.id),
      };

      // Step IV: 篩選符合最早下訂時間
      avaiableCars = filterNonMinDate(avaiableCars, params);
      // console.log("4. 篩選符合最早下訂時間", avaiableCars);
      filterResult = {
        ...filterResult,
        "4_filterNonMinDate": avaiableCars.map((car) => car.id),
      };

      // Step V: 篩選符合營業時間
      avaiableCars = filterNonBusinessTime(avaiableCars, params);
      // console.log("5. 篩選符合營業時間", avaiableCars);
      filterResult = {
        ...filterResult,
        "5_filterNonBusinessTime": avaiableCars.map((car) => car.id),
      };

      // Step VI: 篩選符合前後訂單間隔 + 人力篩選
      avaiableCars = filterNonOrderAndStaffOk(avaiableCars, params);
      // console.log("6. 篩選符合前後訂單間隔 + 人力篩選", avaiableCars);
      filterResult = {
        ...filterResult,
        "6_filterNonOrderAndStaffOk": avaiableCars.map((car) => car.id),
      };

      // Step VII: 前端價格排序
      avaiableCars = sortCars(avaiableCars, sortBy, top_models, groupPrice);
      // console.log("7. 價格排序", avaiableCars);

      // console.log("avaiableCars", avaiableCars);
      // console.log(
      //   "reduceCarsToOneLevelAddVersion",
      //   reduceCarsToOneLevelAddVersion(avaiableCars)
      // );
      // console.log(
      //   "reduceCarsToOneLevelAddColor",
      //   reduceCarsToOneLevelAddColor(avaiableCars)
      // );

      // 群組車輛
      appStore.avaiableCarGroups = reduceCarsToOneLevelAddVersion(avaiableCars);
      // appStore.avaiableCarGroups = reduceCarsToOneLevelAddVersion(avaiableCars);
      // console.log("carList", {
      //   avaiableCarGroups: appStore.avaiableCarGroups,
      //   carsToTwoLevel2: appStore.carsToTwoLevel2,
      // });

      // console.log("carsToTwoLevel2", carsToTwoLevel2);

      if (
        window.location.hostname !== "localhost" &&
        !window?.location?.href?.includes("dev")
      ) {
        const pickupZoneData = deliveryZones.find(
          (zone) => zone.id == selectedZoneId
        );
        const returnZoneData = deliveryZones.find(
          (zone) => zone.id == selectedReturnZoneId
        );

        const searchData = {
          start_date: dateTimeObj.startDate.format("YYYY/MM/DD"),
          start_time: dateTimeObj.startTime,
          end_date: dateTimeObj.endDate.format("YYYY/MM/DD"),
          end_time: dateTimeObj.endTime,
          pickup_zone_id: selectedZoneId,
          pickup_city: pickupZoneData?.level_1,
          pickup_zone: pickupZoneData?.level_2,
          pickup_station: pickupZoneData?.level_3,
          return_zone_id: selectedReturnZoneId,
          return_city: returnZoneData?.level_1,
          return_zone: returnZoneData?.level_2,
          return_station: returnZoneData?.level_3,
          all_car_ids: cars.map((car) => car.id),
          result_car_ids: avaiableCars.map((car) => car.id),
          result_car_group_num: Object.keys(appStore.avaiableCarGroups).length,
          filter_result: filterResult,
          search_testing: getCookie("UDRIVE_T_GOOGLEMAP_SEARCH_v3"),
          modelsFilter: modelsFilter,
        };
        fsEvent(searchData, "search");
      }
    }
  }, [
    dates,
    selectedDays,
    selectedEndClock,
    selectedStartClock,
    configs,
    orders,
    cars,
    modelsFilter,
    // colorSelected,
    sortBy,
    selectedZoneId,
    selectedReturnZoneId,
    top_models,
    groupPrice,
  ]);

  useEffect(() => {
    if (selectedDays.length === 2 && selectedStartClock && selectedEndClock) {
      const st = new DateObject(
        selectedDays[0].format("YYYY/MM/DD") + " " + selectedStartClock
      );
      const et = new DateObject(
        selectedDays[1].format("YYYY/MM/DD") + " " + selectedEndClock
      );
      const data = {
        start_time: st.format("YYYY/MM/DD H:mm"),
        end_time: et.format("YYYY/MM/DD H:mm"),
      };
      api.getGroupPrice(data).then((resp) => {
        setGroupPrice(resp.data.data);
      });
    }
  }, [selectedDays, selectedEndClock, selectedStartClock, router]);

  const isAfter20 = isAfter(new Date(), new Date("2024/06/19 15:59:59"));

  const isAfterSummer = isAfter(new Date(), new Date("2024/09/15 15:59:59"));
  // const isAfterSummer = true;

  // 現在時間 +10 天 < selectedDays[0] 則能免費取消
  const isFreeCancel = isBefore(
    addDays(new Date(), 10),
    new Date(selectedDays[0].format("YYYY/MM/DD"))
  );

  return (
    <Flex
      maxW={{ base: "90%", md: "90%" }}
      justify="center"
      pt={
        !isAfterSummer
          ? { base: "20px", md: "20px" }
          : { base: "182px", md: "180px" }
      }
      // pt={{ base: "20px", md: "20px" }}
      pb="150px"
    >
      <Box flex="1">
        {Object.keys(avaiableCarGroups).length === 0 ? (
          isCarListFetching ? (
            <CarListLoading />
          ) : (
            <Box mb="20px">
              <GCPImage
                src="/images/NoCarImg.svg"
                alt="noCar"
                w="150px"
                mt="10px"
              />
              <Text
                mt="24px"
                color="rgba(51, 51, 51, 1)"
                fontSize="24px"
                fontWeight="700"
                lineHeight="35px"
              >
                {t("noCar")}
              </Text>
              <Text
                mt="8px"
                color="rgba(136, 136, 136, 1)"
                fontSize="14px"
                fontWeight="500"
                lineHeight="20px"
              >
                {t("noCarTip")}
              </Text>
              <MainButton
                mt="20px"
                fontSize="14px"
                lineHeight="1"
                onClick={() => {
                  appStore.isHelperOpen = true;
                }}
              >
                {t("contactUs")}
              </MainButton>

              {/* 如果 colorSelected 物件裡有選項是 true 才顯示 MainButton */}
              {/* {Object.values(colorSelected).includes(true) && (
                <MainButton
                  mt="20px"
                  ml="16px"
                  fontSize="14px"
                  lineHeight="1"
                  onClick={() => {
                    appStore.colorSelected = {
                      white: false,
                      black: false,
                      red: false,
                      silver: false,
                      pink: false,
                      quick_silver: false,
                      blue: false,
                      sky_blue: false,
                    };
                  }}
                >
                  {t("noCarClean")}
                </MainButton>
              )} */}
            </Box>
          )
        ) : (
          <Wrap justify="center" spacingX="16px">
            <WrapItem>
              <Text
                w="358px"
                textAlign="start"
                color="rgba(136, 136, 136, 1)"
                fontWeight="500"
                fontSize={{ base: "12px", md: "16px" }}
                lineHeight={{ base: "17px", md: "23px" }}
              >
                {t("only")} {Object.keys(appStore.avaiableCarGroups).length}{" "}
                {Object.keys(avaiableCarGroups).length > 1
                  ? t("models")
                  : t("model")}{" "}
                {t("canOrderCar")}
              </Text>
            </WrapItem>
            <WrapItem>
              <Box w={{ base: "0px", md: "358px" }}></Box>
            </WrapItem>
            <WrapItem>
              <Box w={{ base: "0px", md: "358px" }}></Box>
            </WrapItem>
            <WrapItem>
              <Box w={{ base: "0px", md: "358px" }}></Box>
            </WrapItem>
          </Wrap>
        )}

        <Wrap justify="center" spacing="16px" mt={{ base: "0px", md: "16px" }}>
          {Object.keys(avaiableCarGroups).map((version) => {
            const carGroup = avaiableCarGroups[version][0];

            let desc = carGroup.description;
            if (desc.includes("@")) {
              desc =
                carGroup.description.split(`@`)[
                  carGroup.description.split(`@`).indexOf(i18n.language) + 1
                ];

              desc = desc.replace(/[\r\n]+/g, " ");
            }
            // 如果carGroup.version包含"座"，將座改成seats
            let title = `${carGroup.brand} ${carGroup.model_name}`;
            let subTitle = carVersionI18n(carGroup.version, t) || "";

            if (carGroup?.version && carGroup?.version.includes("座")) {
              subTitle = `${
                carGroup.version.replace("座", ` ${t("titleSeats")}`) || ""
              }`;
            }

            return (
              <WrapItem key={version}>
                <CarGroup
                  groupPrice={groupPrice}
                  carGroup={avaiableCarGroups[version]}
                  title={title}
                  subTitle={subTitle}
                  description={desc}
                  note={carGroup.note}
                  displayPrice={carGroup.displayPrice}
                  cars={avaiableCarGroups[version]}
                  iconSrc={carGroup.image}
                  isFreeCancel={isFreeCancel}
                  isHot={top_models?.includes(carGroup.model_id)}
                />
              </WrapItem>
            );
          })}
          {/* 顯示非 avaiableCarGroups 的車款 */}
          {/* {carsToTwoLevel2 &&
            Object.keys(carsToTwoLevel2).map((version) => {
              const hasCar = avaiableCarGroups[version];
              if (hasCar) {
                return;
              }

              const carGroup = carsToTwoLevel2[version][0];
              let desc = carGroup.description;
              if (desc.includes("@")) {
                desc =
                  carGroup.description.split(`@`)[
                    carGroup.description.split(`@`).indexOf(i18n.language) + 1
                  ];
                desc = desc.replace(/[\r\n]+/g, " ");
              }

              return (
                <WrapItem key={version}>
                  <NoCarGroup
                    groupPrice={groupPrice}
                    carGroup={carsToTwoLevel2[version]}
                    title={`${carGroup.model_name}`}
                    subTitle={carVersionI18n(carGroup.version, t) || ""}
                    description={desc}
                    note={carGroup.note}
                    displayPrice={carGroup.displayPrice}
                    cars={carsToTwoLevel2[version]}
                    iconSrc={carGroup.image}
                    isFreeCancel={false}
                    isHot={top_models?.includes(carGroup.model_id)}
                  />
                </WrapItem>
              );
            })} */}

          <WrapItem>
            <Box w="358px"></Box>
          </WrapItem>
          <WrapItem>
            <Box w="358px"></Box>
          </WrapItem>
          <WrapItem>
            <Box w="358px"></Box>
          </WrapItem>
        </Wrap>
      </Box>
    </Flex>
  );
}

export default CarList;
